import {
  CurrencyPipe,
  DecimalPipe,
  registerLocaleData,
  TitleCasePipe,
} from '@angular/common';
import {
  HttpBackend,
  provideHttpClient,
  withInterceptors,
} from '@angular/common/http';
import localeDe from '@angular/common/locales/de';
import localeEs from '@angular/common/locales/es';
import localeFr from '@angular/common/locales/fr';
import localeIt from '@angular/common/locales/it';
import localePl from '@angular/common/locales/pl';
import type { ApplicationConfig } from '@angular/core';
import {
  ErrorHandler,
  inject,
  LOCALE_ID,
  provideAppInitializer,
  provideBrowserGlobalErrorListeners,
  provideZonelessChangeDetection,
} from '@angular/core';
import {
  provideRouter,
  Router,
  TitleStrategy,
  withExperimentalAutoCleanupInjectors,
} from '@angular/router';
import { provideTranslateService, TranslateLoader } from '@ngx-translate/core';
import {
  ONYX_HERE_MAPS_API_KEY,
  ONYX_TRANSLATIONS_SOURCE,
  OnyxAddressService,
  OnyxAmountPipe,
  OnyxDatePipe,
  OnyxDictionariesService,
  OnyxFilterPipe,
  OnyxLanguagePipe,
  OnyxNamePipe,
  OnyxPhonePipe,
  OnyxPluralTranslatePipe,
  OnyxPreferencesService,
  OnyxStorageService,
  OnyxTemperaturePipe,
  OnyxThemeService,
  OnyxTimePipe,
  OnyxUserSettingsService,
} from '@onyx/angular';
import { TraceService } from '@sentry/angular';
import { provideAngularSvgIcon } from 'angular-svg-icon';
import { MultiTranslateHttpLoader } from 'ngx-translate-multi-http-loader';
import { environment } from '../config/environment';
import { I18N_NAMESPACES } from '../config/i18n';
import { ROUTES } from './app.routes';
import { AppTitleStrategy } from './common/services/app-title.strategy';
import { authInterceptor } from './auth/common/interceptors/auth.interceptor';
import { GlobalErrorHandler } from './common/handlers/global-error-handler';
import { BusinessHoursPipe } from './common/pipes/business-hours.pipe';
import { DiffPipe } from './common/pipes/diff.pipe';
import { DimensionsPipe } from './common/pipes/dimensions.pipe';
import { DurationPipe } from './common/pipes/duration.pipe';
import { FleetIdentifierPipe } from './common/pipes/fleet-identifier.pipe';
import { GoodQuantityPipe } from './common/pipes/good-quantity.pipe';
import { JoinPipe } from './common/pipes/join.pipe';
import { TelematicsIdentifierPipe } from './common/pipes/telematics-fleet-identifier.pipe';
import { TimeWindowsPipe } from './common/pipes/time-windows.pipe';
import { AddressService } from './common/services/address.service';
import { DictionariesService } from './common/services/dictionaries.service';
import { LanguageService } from './common/services/language.service';
import { PreferencesService } from './common/services/preferences.service';
import { StorageService } from './common/services/storage.service';
import { UserSettingsService } from './common/services/user-settings.service';

registerLocaleData(localeDe);
registerLocaleData(localeEs);
registerLocaleData(localeFr);
registerLocaleData(localeIt);
registerLocaleData(localePl);

const translationsApiUrl = new URL(environment.apiUrl);
translationsApiUrl.host = `${environment.publicApiNamespace}.${translationsApiUrl.host}`;

export const createTranslateLoader = (http: HttpBackend) =>
  new MultiTranslateHttpLoader(http, [
    ONYX_TRANSLATIONS_SOURCE,
    ...I18N_NAMESPACES.map((namespace) => ({
      prefix: `i18n/${namespace}/`,
      suffix: '.json',
    })),
    { prefix: `${translationsApiUrl}/dictionaries/`, suffix: ' ' },
  ]);

export const appConfig: ApplicationConfig = {
  providers: [
    provideZonelessChangeDetection(),
    provideBrowserGlobalErrorListeners(),
    provideRouter(ROUTES, withExperimentalAutoCleanupInjectors()),
    provideHttpClient(withInterceptors([authInterceptor])),
    provideAngularSvgIcon(),
    provideTranslateService({
      loader: {
        provide: TranslateLoader,
        useFactory: createTranslateLoader,
        deps: [HttpBackend],
      },
    }),

    { provide: ErrorHandler, useClass: GlobalErrorHandler },
    {
      provide: LOCALE_ID,
      useFactory: (languageService: LanguageService) =>
        languageService.getLocale(),
      deps: [LanguageService],
    },
    { provide: TitleStrategy, useClass: AppTitleStrategy },
    provideAppInitializer(() => void inject(OnyxThemeService)),

    // ONYX Angular Kit
    OnyxAmountPipe,
    OnyxDatePipe,
    OnyxFilterPipe,
    OnyxLanguagePipe,
    OnyxNamePipe,
    OnyxPhonePipe,
    OnyxPluralTranslatePipe,
    OnyxTemperaturePipe,
    OnyxTimePipe,

    { provide: OnyxAddressService, useClass: AddressService },
    { provide: OnyxDictionariesService, useClass: DictionariesService },
    { provide: OnyxStorageService, useClass: StorageService },
    { provide: OnyxPreferencesService, useClass: PreferencesService },
    { provide: OnyxUserSettingsService, useClass: UserSettingsService },
    { provide: ONYX_HERE_MAPS_API_KEY, useValue: environment.hereMapsApiKey },
    //

    CurrencyPipe,
    DecimalPipe,

    BusinessHoursPipe,
    DiffPipe,
    DimensionsPipe,
    DurationPipe,
    FleetIdentifierPipe,
    GoodQuantityPipe,
    JoinPipe,
    TelematicsIdentifierPipe,
    TimeWindowsPipe,
    TitleCasePipe,

    // Sentry
    { provide: TraceService, deps: [Router] },
    provideAppInitializer(() => void inject(TraceService)),
  ],
};
